import type { Landmark } from '../../../data/types';
import type { LinearRGB } from '../../../core/color';
import type { Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import {
  beamSolid,
  coneSolid,
  horizontalCap,
  prismSolid,
  pyramidOverRing,
  ringPoints,
} from '../solids';
import { buildVessel } from './churchVessel';
import type { VesselPlan } from './churchModel';
import { centredWindow, rectWall, type MasonryStyles } from './masonry';
import { PAINTED_CHAPEL_STYLE } from './paintedChapels';
import { dominantAxis, meshFrameOf, planBounds, planFrameOf } from './planFrame';
import { LANDMARK_PATTERN, patterned } from './surfacePatterns';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** The 1538 chapel is a compound medieval complex, not the present post-1644 church.
 * Archaeologische Kroniek 1991 pp. 328–329 records an original 8×15m chapel, a 1490 extension,
 * and an octagonal Jeruzalem chapel on twelve-sided foundations. The painting confirms unequal
 * gabled roof masses. Part proportions are scaled to the current estimated reserved footprint;
 * unseen sides, window spacing and roof-turret construction remain reconstruction estimates.
 */
export interface SintOlofColors {
  readonly wall: LinearRGB;
  readonly roof: LinearRGB;
  readonly dark: LinearRGB;
  readonly glass: LinearRGB;
  readonly dressing: LinearRGB;
  readonly timber: LinearRGB;
}

interface ComplexPlan {
  readonly main: VesselPlan;
  readonly added: VesselPlan;
  readonly jeruzalem: { readonly centre: Vec2; readonly radius: number; readonly height: number };
}

function complexPlan(landmark: Landmark): { readonly frame: Frame; readonly plan: ComplexPlan } {
  const p = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const b = planBounds(p);
  const length = b.u1 - b.u0;
  const width = b.v1 - b.v0;
  const v = b.v0 + width * 0.36;
  const main: VesselPlan = {
    u0: b.u0 + 0.8,
    chordU: b.u0 + length * 0.6,
    vc: v,
    half: width * 0.255,
    eaveY: 9.2,
    ridgeY: 12.9,
    apse: 'flat',
  };
  const added: VesselPlan = {
    u0: b.u0 + length * 0.12,
    chordU: b.u0 + length * 0.54,
    vc: main.vc + main.half + width * 0.125,
    half: width * 0.125,
    eaveY: 7.8,
    ridgeY: 10.2,
    apse: 'flat',
  };
  return {
    frame: meshFrameOf(p),
    plan: {
      main,
      added,
      jeruzalem: {
        centre: [b.u0 + length * 0.83, b.v0 + width * 0.44],
        radius: Math.min(width * 0.31, length * 0.12),
        height: 6.2,
      },
    },
  };
}

function turret(
  builder: MeshBuilder,
  styles: MasonryStyles,
  timber: VertexStyle,
  frame: Frame,
  centre: Vec2,
  ridge: number
): void {
  const ring = ringPoints(centre, 0.68, 8, Math.PI / 8);
  const foot = ridge - 0.45;
  prismSolid(builder, timber, frame, ring, foot, ridge + 0.45, { top: true, bottom: true });
  const lanternTop = ridge + 2.15;
  for (const p of ring) {
    beamSolid(builder, timber, frame, [p[0], ridge + 0.4, -p[1]], [p[0], lanternTop, -p[1]], 0.13);
  }
  const cap = ringPoints(centre, 0.82, 8, Math.PI / 8);
  prismSolid(builder, timber, frame, cap, lanternTop - 0.12, lanternTop + 0.08, {
    top: false,
    bottom: true,
  });
  pyramidOverRing(builder, styles, frame, cap, lanternTop + 0.08, {
    plan: centre,
    y: lanternTop + 1.9,
  });
  const rod = { center: centre, radius: 0.035, segments: 6, phase: 0 };
  coneSolid(builder, timber, frame, rod, lanternTop + 1.85, lanternTop + 2.6, true);
}

function jeruzalem(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  plan: ComplexPlan['jeruzalem']
): void {
  const { centre, radius, height } = plan;
  const foundation = ringPoints(centre, radius + 0.2, 12, Math.PI / 12);
  prismSolid(builder, styles.wall, frame, foundation, -0.7, 0.45, { top: true, bottom: false });
  const ring = ringPoints(centre, radius, 8, Math.PI / 8);
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    rectWall(builder, styles, frame, [a, b], [0.4, height], centredWindow(width, 1.8, 5.2, 0.44));
  });
  horizontalCap(builder, styles.wall, frame, ring, 0.4, false);
  pyramidOverRing(builder, styles, frame, ring, height, { plan: centre, y: height + 3.3 });
}

/** Builds the paired hall roofs, octagonal pilgrimage chapel and small open timber belfry.
 * Kept inside the current landmark reservation; the original chapel dimensions are archaeological
 * evidence for the component types, not a claim that this estimated footprint is the excavated plan.
 */
export function buildSintOlofskapel(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: SintOlofColors,
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY
): number {
  const before = builder.triangleCount;
  const { frame, plan } = complexPlan(landmark);
  const base = {
    ...ids,
    strokeAngle: 0,
    exaggeration: { base: 0, share: PAINTED_CHAPEL_STYLE.share },
  };
  const wall: VertexStyle = patterned(
    { ...base, color: colors.wall, surface: SURFACE.stone, extent: [20, 10] },
    LANDMARK_PATTERN.lateGothic
  );
  const roof: VertexStyle = patterned(
    { ...base, color: colors.roof, surface: SURFACE.roofSlate, extent: [20, 7] },
    LANDMARK_PATTERN.slate
  );
  const opening: VertexStyle = {
    ...base,
    color: colors.dark,
    surface: SURFACE.linework,
    extent: [1, 1],
  };
  const reveal: VertexStyle = { ...wall, color: colors.dressing };
  const glass: VertexStyle = patterned({ ...opening, color: colors.glass }, LANDMARK_PATTERN.glass);
  const styles: MasonryStyles = {
    wall,
    roof,
    opening,
    glazing: { reveal, leaded: glass, tracery: { sink: tracery, edgeId: ids.edgeId } },
  };
  const timber: VertexStyle = {
    ...base,
    color: colors.timber,
    surface: SURFACE.wood,
    extent: [1, 2],
  };
  const options = { longWindows: { sill: 2.5, top: 7.7 }, westWindow: true, bay: 4.2 };
  buildVessel(builder, styles, frame, plan.main, options);
  buildVessel(builder, styles, frame, plan.added, {
    ...options,
    longWindows: { sill: 2.3, top: 6.5 },
    bay: 4.0,
  });
  // The pilgrimage chapel adjoins the hall complex. The low connecting bay's exact position is
  // an estimate inside the reserved footprint, rather than a claim to its excavated plan.
  buildVessel(
    builder,
    styles,
    frame,
    {
      u0: plan.main.chordU - 0.2,
      chordU: plan.jeruzalem.centre[0] - plan.jeruzalem.radius + 0.35,
      vc: plan.jeruzalem.centre[1],
      half: 1.1,
      eaveY: 5.2,
      ridgeY: 6.8,
      apse: 'flat',
    },
    { longWindows: { sill: 1.8, top: 4.4 }, westWindow: false, bay: 4.2 }
  );
  jeruzalem(builder, styles, frame, plan.jeruzalem);
  turret(builder, styles, timber, frame, [plan.main.u0 + 2.0, plan.main.vc], plan.main.ridgeY);
  return builder.triangleCount - before;
}
